import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Loader2 } from 'lucide-react';
import { ArrowLeft, MagnifyingGlass, Key, User, Trash, Gift } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export function AdminUsersPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [impersonating, setImpersonating] = useState(null);

  useEffect(() => { (async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setUsers(res.data.users || []);
    } catch {
      toast.error('Erreur chargement utilisateurs');
    } finally { setLoading(false); }
  })(); }, []);

  const handleImpersonate = async (user) => {
    if (!window.confirm(`Se connecter en tant que ${user.name || user.email} ?\n\nVous verrez son interface comme s'il était connecté. Vous pourrez revenir à votre compte admin à tout moment via la bannière en haut.`)) return;
    setImpersonating(user.id);
    try {
      await axios.post(`${API}/admin/impersonate/${user.id}`, {}, { withCredentials: true });
      toast.success(`Connecté en tant que ${user.email}`);
      window.location.href = '/dashboard';
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur impersonation');
    } finally { setImpersonating(null); }
  };

  const toggleBoris = async (user) => {
    try {
      const r = await axios.post(`${API}/admin/users/${user.id}/boris-enable`, {}, { withCredentials: true });
      setUsers(prev => prev.map(u => u.id === user.id ? { ...u, boris_enabled: r.data.boris_enabled } : u));
      toast.success(r.data.boris_enabled ? `Gaby activé pour ${user.email}` : `Gaby désactivé pour ${user.email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur toggle Gaby');
    }
  };

  const toggleChantixAI = async (user) => {
    try {
      const r = await axios.post(`${API}/admin/users/${user.id}/chantix-ai-enable`, {}, { withCredentials: true });
      setUsers(prev => prev.map(u => u.id === user.id ? { ...u, chantix_ai_enabled: r.data.chantix_ai_enabled } : u));
      toast.success(r.data.chantix_ai_enabled ? `AXEL activé pour ${user.email}` : `AXEL désactivé pour ${user.email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur toggle AXEL');
    }
  };

  const [deleting, setDeleting] = useState(null);
  const [giftTarget, setGiftTarget] = useState(null);
  const [plans, setPlans] = useState([]);

  // Charge les plans une fois pour le modal cadeau
  useEffect(() => { (async () => {
    try {
      const r = await axios.get(`${API}/subscriptions/plans`, { withCredentials: true });
      setPlans(r.data || []);
    } catch { /* silencieux */ }
  })(); }, []);
  const deleteUser = async (user) => {
    if (!window.confirm(`⚠️ SUPPRIMER DÉFINITIVEMENT\n\n${user.email}\n\nCette action supprime aussi TOUS ses chantiers, réserves, lots, photos, notes, docs, abonnements, factures, comptes-rendus… (cascade complète).\n\nAction IRRÉVERSIBLE. Continuer ?`)) return;
    if (!window.confirm(`Dernière confirmation : supprimer ${user.email} ?`)) return;
    setDeleting(user.id);
    try {
      const res = await axios.post(
        `${API}/admin/users/delete-by-email?email=${encodeURIComponent(user.email)}&dry_run=false`,
        {},
        { withCredentials: true, headers: { 'X-Confirm-Token': 'DELETE-USER-2026' } }
      );
      const nb = Object.values(res.data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`${user.email} supprimé (${nb} enregistrements cascade)`);
      setUsers(prev => prev.filter(u => u.id !== user.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur suppression');
    } finally { setDeleting(null); }
  };

  const INTERNAL_EMAILS = new Set(['admin@chantix.fr', 'testeur@chantix.fr']);

  const filtered = users.filter(u => {
    // Masquer les comptes internes (admin + testeur) des stats/listes
    if (INTERNAL_EMAILS.has((u.email || '').toLowerCase())) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (u.email || '').toLowerCase().includes(q) || (u.name || '').toLowerCase().includes(q);
  });

  const subBadge = (sub) => {
    if (!sub) return <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">Aucun abo</span>;
    const status = sub.status;
    const colors = {
      trialing: 'bg-amber-100 text-amber-700',
      active: 'bg-green-100 text-green-700',
      expired: 'bg-red-100 text-red-600',
      cancelled: 'bg-gray-100 text-gray-600',
    };
    return <span className={`text-xs px-2 py-0.5 rounded-full ${colors[status] || 'bg-gray-100 text-gray-600'}`}>{status || '?'}</span>;
  };

  return (
    <AppShell>
      <div className="space-y-6" data-testid="admin-users-page">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="icon" className="h-10 w-10 md:hidden" onClick={() => navigate('/dashboard')}>
              <ArrowLeft size={22} />
            </Button>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-[#111827] truncate">Utilisateurs CHANTIX</h1>
              <p className="text-sm text-[#4B5563]">{filtered.length} utilisateur(s) affichés {filtered.length !== users.length && <span className="text-xs text-gray-400">· {users.length - filtered.length} compte(s) interne(s) masqué(s)</span>} — Cliquez sur &laquo;&nbsp;Se connecter comme&nbsp;&raquo; pour assister un pilote</p>
            </div>
          </div>
          <div className="relative">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher email/nom..."
              className="pl-9 w-full sm:w-72"
              data-testid="admin-users-search"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#0055FF]" /></div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr className="text-left text-xs text-gray-600 uppercase">
                  <th className="px-4 py-3 font-medium">Utilisateur</th>
                  <th className="px-4 py-3 font-medium hidden md:table-cell">Profil</th>
                  <th className="px-4 py-3 font-medium hidden sm:table-cell">Abonnement</th>
                  <th className="px-4 py-3 font-medium hidden lg:table-cell">Inscription</th>
                  <th className="px-4 py-3 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filtered.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-12 text-center text-gray-400">Aucun utilisateur trouvé</td></tr>
                )}
                {filtered.map(u => (
                  <tr key={u.id} className="hover:bg-gray-50 transition" data-testid={`user-row-${u.email}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#0055FF] to-[#FF6B00] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                          {(u.name || u.email || '?').charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-[#111827] truncate">{u.name || '—'}</p>
                          <p className="text-xs text-gray-500 truncate">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-xs uppercase text-gray-600">{u.profile_type || '—'}</td>
                    <td className="px-4 py-3 hidden sm:table-cell">{subBadge(u.subscription)}</td>
                    <td className="px-4 py-3 hidden lg:table-cell text-xs text-gray-500">{(u.created_at || '').slice(0, 10)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => toggleBoris(u)}
                          className={`text-xs px-2 py-1 rounded-full font-semibold transition flex items-center gap-1
                            ${u.boris_enabled ? 'bg-amber-500 text-white hover:bg-amber-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                          data-testid={`toggle-boris-${u.email}`}
                          title={u.boris_enabled ? 'Désactiver Gaby pour cet utilisateur' : 'Activer Gaby pour cet utilisateur'}
                        >
                          👷 Gaby {u.boris_enabled ? 'ON' : 'OFF'}
                        </button>
                        <button
                          onClick={() => toggleChantixAI(u)}
                          className={`text-xs px-2 py-1 rounded-full font-semibold transition flex items-center gap-1
                            ${u.chantix_ai_enabled ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                          data-testid={`toggle-chantix-ai-${u.email}`}
                          title={u.chantix_ai_enabled ? 'Désactiver AXEL pour cet utilisateur' : 'Activer AXEL pour cet utilisateur'}
                        >
                          ✨ AXEL {u.chantix_ai_enabled ? 'ON' : 'OFF'}
                        </button>
                        <button
                          onClick={() => setGiftTarget(u)}
                          className="text-xs px-2 py-1 rounded-full font-semibold bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition flex items-center gap-1"
                          data-testid={`gift-sub-${u.email}`}
                          title="Offrir un abonnement gratuit"
                        >
                          🎁 Offrir
                        </button>
                        <Button
                          size="sm" variant="outline"
                          onClick={() => handleImpersonate(u)}
                          disabled={impersonating === u.id}
                          className="text-xs border-[#0055FF] text-[#0055FF] hover:bg-[#0055FF]/10"
                          data-testid={`impersonate-btn-${u.email}`}
                        >
                          {impersonating === u.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Key size={14} className="mr-1" />}
                          Se connecter comme
                        </Button>
                        <Button
                          size="sm" variant="outline"
                          onClick={() => deleteUser(u)}
                          disabled={deleting === u.id}
                          className="text-xs border-red-300 text-red-600 hover:bg-red-50"
                          data-testid={`delete-user-${u.email}`}
                          title="Supprimer définitivement cet utilisateur + toutes ses données"
                        >
                          {deleting === u.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash size={14} />}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {/* Modal Offrir abonnement gratuit */}
      {giftTarget && (
        <GiftSubscriptionDialog
          target={giftTarget}
          plans={plans}
          onClose={() => setGiftTarget(null)}
          onSuccess={(msg) => {
            toast.success(msg);
            setGiftTarget(null);
            // Refresh users
            axios.get(`${API}/admin/users`, { withCredentials: true }).then(r => setUsers(r.data.users || []));
          }}
        />
      )}
    </AppShell>
  );
}

// ==== Modal d'offrande d'abonnement gratuit ====
function GiftSubscriptionDialog({ target, plans, onClose, onSuccess }) {
  const [planId, setPlanId] = useState(plans[0]?.id || '');
  const [duration, setDuration] = useState(1);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!planId) { toast.error('Choisissez un plan'); return; }
    setSaving(true);
    try {
      const r = await axios.post(
        `${API}/subscriptions/admin/grant/${target.id}`,
        { plan_id: planId, duration_months: duration, note },
        { withCredentials: true }
      );
      onSuccess(
        r.data.is_unlimited
          ? `Abonnement "${r.data.plan_name}" offert à ${r.data.target_email} en accès illimité`
          : `Abonnement "${r.data.plan_name}" offert à ${r.data.target_email} pour ${r.data.duration_months} mois`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby="gift-desc">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gift size={22} className="text-emerald-600" />
            Offrir un abonnement gratuit
          </DialogTitle>
          <p id="gift-desc" className="text-sm text-[#6B7280]">
            Bénéficiaire : <strong>{target.name || target.email}</strong> ({target.email})
          </p>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <label className="text-sm font-medium block mb-1">Plan à offrir</label>
            <Select value={planId} onValueChange={setPlanId}>
              <SelectTrigger className="h-11" data-testid="gift-plan-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {plans.map(p => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name} — {p.price} €/mois
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">Durée</label>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setDuration(0)}
                className={`px-3 py-2 rounded-lg text-sm font-medium border transition ${duration === 0 ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                data-testid="gift-duration-unlimited">
                ♾️ Illimité
              </button>
              <div className="flex-1 relative">
                <Input type="number" min={1} max={600}
                  value={duration === 0 ? '' : duration}
                  onChange={e => setDuration(parseInt(e.target.value) || 1)}
                  disabled={duration === 0}
                  placeholder={duration === 0 ? 'À vie' : 'Nombre de mois'}
                  className="pr-12 h-11"
                  data-testid="gift-duration-input" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 pointer-events-none">
                  {duration === 0 ? '' : 'mois'}
                </span>
              </div>
            </div>
            {/* Raccourcis rapides */}
            <div className="flex gap-1 mt-2 flex-wrap">
              {[1, 3, 6, 12, 24, 60].map(m => (
                <button key={m} type="button" onClick={() => setDuration(m)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition ${duration === m ? 'bg-emerald-100 text-emerald-700 border-emerald-300 font-semibold' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                  data-testid={`gift-duration-preset-${m}`}>
                  {m} mois{m === 12 ? ' (1 an)' : m === 24 ? ' (2 ans)' : m === 60 ? ' (5 ans)' : ''}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">Motif (interne, optionnel)</label>
            <Textarea rows={2} placeholder="Ex : beta-testeur pilote Réunion, partenaire OPC, cadeau commercial…"
              value={note} onChange={e => setNote(e.target.value)} maxLength={500}
              data-testid="gift-note" />
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-800">
            ✅ L&apos;utilisateur aura un accès complet immédiat {duration === 0 ? <><strong>à vie (durée illimitée)</strong></> : <>pendant <strong>{duration} mois</strong></>}. Aucun paiement demandé.
            Le renouvellement automatique est <strong>désactivé</strong>{duration !== 0 && ' — vous devrez relancer manuellement à l\'expiration'}.
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={onClose}>Annuler</Button>
            <Button onClick={submit} disabled={saving || !planId}
              className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="gift-confirm">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Gift size={16} className="mr-1" />}
              Offrir l&apos;abonnement
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
